import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { COLLECTIONS } from "./collections";

/**
 * ACTIONS - Tipos padronizados de ações
 */
export const AUDIT_ACTIONS = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
    LOGIN: 'LOGIN',
    LOGOUT: 'LOGOUT',
    EXPORT: 'EXPORT',
    OTHER: 'OTHER'
};

/**
 * MODULES - Módulos do sistema
 */
export const AUDIT_MODULES = {
    ROLES: 'Cargos',
    EMPLOYEES: 'Colaboradores',
    MEMBERS: 'Clientes',
    CLASSES: 'Turmas',
    SESSIONS: 'Sessões',
    ACTIVITIES: 'Atividades',
    LEVELS: 'Níveis',
    AREAS: 'Áreas',
    FINANCIAL: 'Financeiro',
    SETTINGS: 'Configurações',
    AUTH: 'Autenticação',
    SYSTEM: 'Sistema',
    MEMBERSHIPS: 'Planos / Contratos',
    SERVICES: 'Produtos e Serviços',
    WORKOUTS: 'Treinos',
    BANK_ACCOUNTS: 'Contas Bancárias',
    ACQUIRERS: 'Adquirentes',
    SALES: 'Vendas',
    FINANCIAL_RECORDS: 'Financeiro (Contas)',
    INTEGRATIONS: 'Integrações',
    GAMIFICATION: 'Gamificação',
    EVENTS: 'Eventos',
    ENROLLMENTS: 'Matrículas',
    EVALUATIONS: 'Avaliações',
    PHYSICAL_TESTS: 'Testes Físicos',
    MARKETING: 'Marketing / Parceiros',
    TENANTS: 'Empresa / Tenant',
    AUTOMATIONS: 'Automações',
    TASKS: 'Tarefas',
    FINANCIAL_CATEGORIES: 'Categorias Financeiras',
};

/**
 * Campos internos/de sistema que nunca devem aparecer no diff.
 * São metadados de auditoria do próprio documento, não dados de negócio.
 */
const IGNORED_DIFF_KEYS = [
    'id', 'idTenant', 'searchName',
    'createdAt', 'updatedAt', 'createdBy', 'updatedBy'
];

/**
 * AuditService
 * Serviço centralizado e ENXUTO para registro de logs de auditoria.
 * Salva apenas o essencial: quem, o quê, quando e o que mudou.
 */
class AuditService {
    /**
     * Registra um log de auditoria.
     * 
     * @param {string} params.idTenant - Tenant
     * @param {Object} params.user - { uid, email, displayName }
     * @param {string} params.module - Módulo (AUDIT_MODULES)
     * @param {string} params.action - Ação (AUDIT_ACTIONS)
     * @param {string} [params.entityId] - ID da entidade afetada
     * @param {string} [params.entityName] - Nome da entidade (desnormalizado)
     * @param {string} params.description - Descrição legível
     * @param {Object} [params.details] - Detalhes extras (diff enxuto)
     */
    static async log({ idTenant, user, module, action, entityId, entityName, description, details = null }) {
        try {
            if (!idTenant || !user?.uid) {
                console.warn("[Audit] Dados insuficientes para log.", { idTenant, uid: user?.uid });
                return false;
            }

            const logData = {
                idTenant,
                userId: user.uid,
                userName: user.displayName || user.fullName || user.email || 'Sistema',
                module: module || 'Sistema',
                action: action || 'OTHER',
                entityId: entityId || null,
                entityName: entityName || null,
                description: description || '',
                details: details || null,
                createdAt: serverTimestamp()
            };

            await addDoc(collection(db, "tenants", idTenant, COLLECTIONS.AUDIT_LOGS), logData);
            return true;
        } catch (error) {
            console.error("[Audit] Erro ao registrar log:", error);
            return false;
        }
    }

    /**
     * Calcula diff ENXUTO entre dois objetos.
     * - Ignora campos de sistema (id, timestamps, etc).
     * - Para objetos aninhados (ex: permissions), faz diff profundo e retorna SÓ o que mudou.
     * - Remove undefined (Firebase não aceita).
     * 
     * @returns {Object} Ex: { name: { from: "A", to: "B" }, permissions: { grade_manage: { from: false, to: true } } }
     */
    static calculateDiff(oldObj, newObj) {
        if (!oldObj || !newObj) return null;

        const changes = {};
        const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

        allKeys.forEach((key) => {
            if (IGNORED_DIFF_KEYS.includes(key)) return;

            const oldVal = oldObj[key];
            const newVal = newObj[key];

            // Se ambos são objetos (ex: permissions), faz diff profundo
            if (oldVal && newVal && typeof oldVal === 'object' && typeof newVal === 'object' && !Array.isArray(oldVal)) {
                const subDiff = {};
                const subKeys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);

                subKeys.forEach(subKey => {
                    const ov = oldVal[subKey];
                    const nv = newVal[subKey];
                    if (JSON.stringify(ov) !== JSON.stringify(nv)) {
                        subDiff[subKey] = {
                            from: ov === undefined ? null : ov,
                            to: nv === undefined ? null : nv
                        };
                    }
                });

                // Só adiciona se algo realmente mudou
                if (Object.keys(subDiff).length > 0) {
                    changes[key] = subDiff;
                }
                return;
            }

            // Comparação simples para primitivos
            if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
                changes[key] = {
                    from: oldVal === undefined ? null : oldVal,
                    to: newVal === undefined ? null : newVal
                };
            }
        });

        // Retorna null se nada mudou (evita salvar objeto vazio no Firebase)
        return Object.keys(changes).length > 0 ? changes : null;
    }

    /**
     * Busca os logs de auditoria com paginação e filtros.
     * 
     * @param {string} idTenant - OBRIGATÓRIO: ID do tenant.
     * @param {Object} options - Opções de busca:
     *   - {number} limit: Quantidade de registros por página (default: 50)
     *   - {Object} lastVisible: Último documento da listagem anterior (para paginação)
     *   - {Object} filters: Filtros opcionais { module, action, startDate, endDate, userId }
     */
    static async getLogs(idTenant, { limit: limitCount = 50, lastVisible = null, filters = {} } = {}) {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório para buscar logs.");

        try {
            const { query, collection, where, orderBy, limit, startAfter, getDocs, Timestamp } = await import("firebase/firestore");
            const logsRef = collection(db, "tenants", idTenant, COLLECTIONS.AUDIT_LOGS);
            
            let constraints = [];

            // Adiciona filtros se existirem
            if (filters.module) constraints.push(where("module", "==", filters.module));
            if (filters.action) constraints.push(where("action", "==", filters.action));
            if (filters.userId) constraints.push(where("userId", "==", filters.userId));
            
            if (filters.startDate) {
                constraints.push(where("createdAt", ">=", Timestamp.fromDate(new Date(filters.startDate))));
            }
            if (filters.endDate) {
                // Configura para o final do dia
                const end = new Date(filters.endDate);
                end.setHours(23, 59, 59, 999);
                constraints.push(where("createdAt", "<=", Timestamp.fromDate(end)));
            }

            // Ordenação padrão (mais recentes primeiro)
            constraints.push(orderBy("createdAt", "desc"));

            // Limite e paginação
            if (lastVisible) constraints.push(startAfter(lastVisible));
            constraints.push(limit(limitCount));

            const q = query(logsRef, ...constraints);
            const snapshot = await getDocs(q);

            const logs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                // Converte Timestamp para Data JS
                createdAt: doc.data().createdAt?.toDate() || null
            }));

            // Retorna a lista e o lastVisible
            return {
                data: logs,
                lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
                hasMore: snapshot.docs.length === limitCount
            };
        } catch (error) {
            console.error("[AuditService.getLogs] Erro:", error);
            throw error;
        }
    }
}

export default AuditService;
